export default function PrivacyPolicyPage() {
    return (
        <div className="prose prose-invert prose-sm max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-100">Privacy Policy</h1>
            <p className="text-xs text-slate-500">Last updated: March 6, 2026</p>

            <h2>1. Introduction</h2>
            <p>
                DebtBound ("we," "our," or "us") operates the DebtBound platform, a
                consensual financial domination contract management application. This
                Privacy Policy explains how we collect, use, disclose, and safeguard
                your information when you use our service.
            </p>

            <h2>2. Information We Collect</h2>
            <h3>Account Information</h3>
            <ul>
                <li>Email address</li>
                <li>Display name</li>
                <li>Password (stored securely using one-way hashing)</li>
                <li>Role selection (dominant, submissive, switch)</li>
                <li>Profile information (bio, avatar, social links)</li>
            </ul>

            <h3>Third-Party Authentication</h3>
            <p>
                When you sign in or link accounts via third-party providers (Google,
                Discord, GitHub, Twitter/X, Microsoft), we receive your
                provider user ID, email, and display name. We store OAuth tokens
                securely (encrypted at rest) solely for authentication purposes.
            </p>

            <h3>Contract &amp; Financial Data</h3>
            <p>
                All contract terms, ledger entries, payment records, and financial
                data you enter into DebtBound are stored to provide the service.
                <strong> DebtBound does not process real financial transactions.</strong>{' '}
                All monetary amounts tracked within contracts are for record-keeping
                and gamification purposes between consenting parties. Actual payments
                occur outside the platform via the funding sources agreed upon between
                parties.
            </p>

            <h3>Usage Data</h3>
            <ul>
                <li>IP addresses and browser user agent (for security and rate limiting)</li>
                <li>Error reports and performance telemetry (via Grafana, for debugging and stability)</li>
                <li>Application performance metrics (via Laravel Pulse)</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <ul>
                <li>To create and manage your account</li>
                <li>To facilitate contracts between consenting parties</li>
                <li>To send notifications (email and in-app) about contract activity</li>
                <li>To display public profiles and contract information you choose to make visible</li>
                <li>To maintain platform security and prevent abuse</li>
                <li>To improve and debug the service</li>
            </ul>

            <h2>4. Information Sharing &amp; Disclosure</h2>
            <p>We do not sell your personal information. We may share information in the following cases:</p>
            <ul>
                <li>
                    <strong>Between contract parties:</strong> Doms and subs within a
                    contract can see each other's display names, profile information,
                    and contract-related data.
                </li>
                <li>
                    <strong>Public profiles:</strong> If you set your profile to public,
                    other users can view your display name, bio, social links, linked
                    accounts (if enabled), reviews, and any contracts marked as public.
                </li>
                <li>
                    <strong>Public feeds:</strong> Certain activities (Wall of Shame,
                    Pillory, Fee/Tax Feed) are visible to all users when enabled for a
                    contract.
                </li>
                <li>
                    <strong>Legal requirements:</strong> We may disclose information
                    if required by law, subpoena, or legal process.
                </li>
            </ul>

            <h2>5. Data Security</h2>
            <p>
                We implement industry-standard security measures including encrypted
                connections (HTTPS), hashed passwords, encrypted OAuth tokens, and
                rate limiting. However, no method of electronic storage is 100% secure,
                and we cannot guarantee absolute security.
            </p>

            <h2>6. Data Retention</h2>
            <p>
                Your account data is retained as long as your account is active.
                Closed and abandoned contracts are retained for historical record
                purposes. You may request deletion of your account and associated
                data by contacting us.
            </p>

            <h2>7. Your Rights</h2>
            <ul>
                <li>Access and review your personal data</li>
                <li>Update or correct your profile information</li>
                <li>Control profile visibility (public/private)</li>
                <li>Choose whether to display linked accounts</li>
                <li>Request deletion of your account</li>
                <li>Unlink third-party accounts at any time</li>
            </ul>

            <h2>8. Cookies &amp; Local Storage</h2>
            <p>
                We use authentication tokens stored in your browser's local storage
                to maintain your login session. We do not use tracking cookies or
                third-party advertising cookies.
            </p>

            <h2>9. Age Requirement</h2>
            <p>
                DebtBound is intended for adults aged 18 and older. We do not
                knowingly collect information from individuals under 18. If we learn
                we have collected data from someone under 18, we will delete that
                information promptly.
            </p>

            <h2>10. Changes to This Policy</h2>
            <p>
                We may update this Privacy Policy from time to time. We will notify
                users of material changes via in-app notification or email. Continued
                use of the service after changes constitutes acceptance of the updated
                policy.
            </p>

            <h2>11. Contact</h2>
            <p>
                If you have questions about this Privacy Policy or wish to exercise
                your data rights, please contact the platform administrator.
            </p>
        </div>
    );
}
